import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { giraffeTrip, giraffeSettings } from '@/lib/giraffePaths';
import { Calendar, Pencil, Settings, Trash2, Archive, Plus } from 'lucide-react';
import { useConfirmation } from '../components/shared/ZCard';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import ZTabs from '@/components/shared/ZTabs';
import OverflowMenu from '@/components/shared/OverflowMenu';
import GiraffeCountriesTab from '@/components/giraffe/GiraffeCountriesTab';
import GiraffeMemoirsTab from '@/components/giraffe/GiraffeMemoirsTab';
import GiraffePostcardsTab from '@/components/giraffe/GiraffePostcardsTab';
import GiraffeArchiveTab from '@/components/giraffe/GiraffeArchiveTab';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';

const statusColors = {
  planning: 'bg-blue-500/20 text-blue-400',
  active: 'bg-emerald-500/20 text-emerald-400',
  complete: 'bg-amber-500/20 text-amber-400',
  completed: 'bg-amber-500/20 text-amber-400',
  published: 'bg-[#6B35B8]/20 text-[#9B6DD4]',
};

const TABS = [
  { id: 'active', label: 'ACTIVE TRIPS' },
  { id: 'countries', label: 'COUNTRIES' },
  { id: 'postcards', label: 'POSTCARDS' },
  { id: 'memoirs', label: 'MEMOIRS' },
  { id: 'archive', label: 'ARCHIVE' },
];

export default function Giraffe() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', start_date: '', end_date: '' });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [archiveConfirm, setArchiveConfirm] = useState({ open: false, trip: null });
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    return ['active', 'countries', 'postcards', 'memoirs', 'archive'].includes(t) ? t : 'active';
  });
  const navigate = useNavigate();
  const feedback = useConfirmation();

  useEffect(() => {
    // Auto-redirect to active trip on first session load (only when on default tab).
    const alreadyRedirected = sessionStorage.getItem('giraffe_initial_redirect');
    if (!alreadyRedirected && activeTab === 'active') {
      sessionStorage.setItem('giraffe_initial_redirect', '1');
      loadTrips(true);
    } else {
      loadTrips(false);
    }
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.detail?.app === 'giraffe') loadTrips(); };
    window.addEventListener('universal-input-applied', handler);
    return () => window.removeEventListener('universal-input-applied', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.app === 'giraffe' && e.detail?.mode === 'trip') setShowNew(true);
    };
    window.addEventListener('fab-new-entry', handler);
    return () => window.removeEventListener('fab-new-entry', handler);
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'active') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tab);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const loadTrips = async (redirect = false) => {
    try {
      const { data } = await api.get('/trips');
      setTrips(data);
      if (redirect) {
        const today = new Date().toISOString().slice(0, 10);
        const ongoing = data.find(t => t.status !== 'archived' && (t.effective_status || t.status) === 'active');
        if (ongoing) { navigate(giraffeTrip(ongoing.id), { replace: true }); return; }
      }
    } catch {
      // silent fail on load
    } finally {
      setLoading(false);
    }
  };

  const createTrip = async () => {
    if (!form.title) return;
    try {
      const { data: newTrip } = await api.post('/trips', form);
      setTrips(prev => [...prev, newTrip]);
      setShowNew(false);
      setForm({ title: '', description: '', start_date: '', end_date: '' });
      feedback.confirm();
    } catch {
      feedback.fail();
    }
  };

  const deleteTrip = async (tripId) => {
    // Optimistic
    const prev = trips;
    setTrips(p => p.filter(t => t.id !== tripId));
    try {
      await api.delete(`/trips/${tripId}`);
      feedback.confirm();
    } catch {
      setTrips(prev);
      feedback.fail();
    }
  };

  const archiveTrip = async (trip) => {
    // Optimistic
    const prev = trips;
    setTrips(p => p.map(t => t.id === trip.id ? { ...t, status: 'archived' } : t));
    setArchiveConfirm({ open: false, trip: null });
    try {
      await api.put(`/trips/${trip.id}`, { status: 'archived' });
      feedback.confirm();
    } catch {
      setTrips(prev);
      feedback.fail();
    }
  };

  const unarchiveTrip = async (trip) => {
    // Restore to planning unless trip dates indicate it's currently active
    const today = new Date().toISOString().slice(0, 10);
    let newStatus = 'planning';
    if (trip.start_date && trip.end_date && trip.start_date <= today && today <= trip.end_date) {
      newStatus = 'active';
    }
    const prev = trips;
    setTrips(p => p.map(t => t.id === trip.id ? { ...t, status: newStatus } : t));
    try {
      await api.put(`/trips/${trip.id}`, { status: newStatus });
      feedback.confirm();
    } catch {
      setTrips(prev);
      feedback.fail();
    }
  };

  const getImageUrl = (url) => url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

  const activeTrips = trips.filter(t => t.status !== 'archived');

  if (loading) return (
    <div style={{ padding: '48px 24px' }}>
      <ZLoadingBar accent="#10B981" loading={true} />
    </div>
  );

  return (
    <div className="p-6 md:p-12 max-w-5xl mx-auto" data-testid="giraffe-page">
      {/* App header */}
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="flex items-center mb-1" style={{ gap: 4 }}>
            <img
              src="/logo-giraffe.png"
              alt="Giraffe"
              style={{ width: 36, height: 36, clipPath: 'url(#squircle)', flexShrink: 0 }}
            />
            <span style={{
              fontFamily: 'system-ui, sans-serif',
              fontSize: 28, fontWeight: 300,
              letterSpacing: '0.18em', textTransform: 'uppercase',
              color: '#f0f0f5', lineHeight: 1
            }}>IRAFFE</span>
          </div>
          <p className="text-[#b0b4c0] text-xs tracking-[0.2em] uppercase font-mono" style={{ paddingLeft: 42 }}>
            The Journey Unraveled
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: 24 }}>
        <ZTabs tabs={TABS} activeTab={activeTab} onChange={handleTabChange} accent="#10B981" />
      </div>

      {/* Content area */}
      {activeTab === 'active' && (
        <ActiveTripsPanel
          trips={activeTrips}
          getImageUrl={getImageUrl}
          onNavigate={(id) => navigate(giraffeTrip(id))}
          onSettings={(id) => navigate(giraffeSettings(id))}
          onArchive={(trip) => setArchiveConfirm({ open: true, trip })}
          onDelete={(trip) => setDeleteConfirm({ open: true, id: trip.id, name: trip.title })}
          onNewTrip={() => setShowNew(true)}
        />
      )}

      {activeTab === 'countries' && <GiraffeCountriesTab />}

      {activeTab === 'postcards' && <GiraffePostcardsTab />}

      {activeTab === 'memoirs' && <GiraffeMemoirsTab />}

      {activeTab === 'archive' && (
        <GiraffeArchiveTab
          trips={trips}
          onUnarchive={unarchiveTrip}
          onDelete={(trip) => setDeleteConfirm({ open: true, id: trip.id, name: trip.title })}
        />
      )}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-white/[0.03] border-white/[0.06] text-[#f0f0f5]">
          <DialogHeader>
            <DialogTitle className="font-serif">New Trip</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              data-testid="trip-title-input"
              placeholder="Where to?"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70"
            />
            <Textarea
              data-testid="trip-desc-input"
              placeholder="A few words about this journey..."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70 min-h-[80px]"
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#b0b4c0] mb-1 block font-light">Start date</label>
                <Input
                  data-testid="trip-start-input"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5]"
                />
              </div>
              <div>
                <label className="text-xs text-[#b0b4c0] mb-1 block font-light">End date</label>
                <Input
                  data-testid="trip-end-input"
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5]"
                />
              </div>
            </div>
            <Button
              data-testid="trip-save-btn"
              onClick={createTrip}
              disabled={!form.title}
              className="w-full bg-[#10B981] hover:bg-[#10B981]/90 text-white rounded-full"
            >
              Create Trip
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null, name: '' })}
        onConfirm={() => deleteTrip(deleteConfirm.id)}
        itemName={deleteConfirm.name}
        itemType="trip"
      />

      {/* Archive confirmation dialog */}
      <Dialog open={archiveConfirm.open} onOpenChange={(open) => !open && setArchiveConfirm({ open: false, trip: null })}>
        <DialogContent className="bg-white/[0.03] border-white/[0.06] text-[#f0f0f5]">
          <DialogHeader>
            <DialogTitle className="font-serif">Archive trip?</DialogTitle>
          </DialogHeader>
          <p className="text-[#b0b4c0] text-sm mt-2">
            Archive "{archiveConfirm.trip?.title}"? It will move to the Archive tab and no longer appear here.
          </p>
          <div className="flex gap-3 mt-6">
            <Button
              onClick={() => setArchiveConfirm({ open: false, trip: null })}
              className="flex-1 bg-transparent border border-white/[0.10] text-[#b0b4c0] hover:bg-white/[0.05] rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={() => archiveTrip(archiveConfirm.trip)}
              className="flex-1 bg-[#10B981] hover:bg-[#10B981]/90 text-white rounded-full"
            >
              Archive
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewTripCard({ onClick }) {
  return (
    <div
      data-testid="trip-card-new"
      onClick={onClick}
      className="group relative bg-white/[0.02] border border-dashed border-white/[0.10] rounded-xl overflow-hidden cursor-pointer hover:border-[#10B981]/50 hover:bg-white/[0.04] transition-all duration-500 flex items-center justify-center min-h-[232px]"
    >
      <div className="flex flex-col items-center gap-3 text-[#b0b4c0] group-hover:text-[#10B981] transition-colors">
        <div className="w-12 h-12 rounded-full border border-white/[0.10] group-hover:border-[#10B981]/50 flex items-center justify-center transition-colors">
          <Plus size={22} strokeWidth={1.5} />
        </div>
        <span className="text-sm font-mono tracking-[0.2em] uppercase">New Trip</span>
      </div>
    </div>
  );
}

function ActiveTripsPanel({ trips, getImageUrl, onNavigate, onSettings, onArchive, onDelete, onNewTrip }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="giraffe-trips-grid">
      {trips.map((trip) => (
        <div
          key={trip.id}
          data-testid={`trip-card-${trip.id}`}
          onClick={() => onNavigate(trip.id)}
          className="group relative bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden cursor-pointer hover:border-[#10B981]/30 transition-all duration-500"
          style={{ position: 'relative' }}
        >
          <div onClick={e => e.stopPropagation()}>
            <OverflowMenu items={[
              { icon: <Pencil size={16} strokeWidth={1.5} />, label: 'Edit', onClick: () => onNavigate(trip.id) },
              { icon: <Settings size={16} strokeWidth={1.5} />, label: 'Settings', onClick: () => onSettings(trip.id) },
              { icon: <Archive size={16} strokeWidth={1.5} />, label: 'Archive', onClick: () => onArchive(trip) },
              { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => onDelete(trip), destructive: true },
            ]} />
          </div>
          <div className="h-36 relative overflow-hidden">
            {trip.cover_image ? (
              <img src={getImageUrl(trip.cover_image)} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#10B981]/20 via-[#0a0a0f] to-[#0a0a0f]" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] to-transparent" />
            <div className="absolute top-3 left-3">
              <span className={`text-xs font-mono tracking-wider uppercase px-2 py-1 rounded-full ${statusColors[trip.effective_status || trip.status] || statusColors.planning}`}>
                {trip.effective_status || trip.status || 'planning'}
              </span>
            </div>
          </div>
          <div className="p-5 -mt-4 relative z-10">
            <h3 className="text-lg text-[#f0f0f5] font-serif mb-1">{trip.title}</h3>
            {trip.description && (
              <p className="text-[#b0b4c0] font-light text-sm line-clamp-2 mb-3">{trip.description}</p>
            )}
            <div className="flex items-center gap-4 text-[#b0b4c0]/70">
              {trip.start_date && (
                <span className="flex items-center gap-1 text-xs font-mono">
                  <Calendar size={12} />
                  {trip.start_date}{trip.end_date ? ` — ${trip.end_date}` : ''}
                </span>
              )}
              {!trip.start_date && (
                <span className="text-xs font-mono">
                  {new Date(trip.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
      <NewTripCard onClick={onNewTrip} />
    </div>
  );
}
